import { useState, useEffect } from 'react'
import styles from './ClientModal.module.css'
import apiClient from '../../utils/api'
import useEscapeKey from '../../hooks/useEscapeKey'

export default function ClientModal({ 
    isOpen, 
    onClose, 
    onSave,
    initialData = null,
    isEditMode = false,
    apiToken = null
}) {
    const [formData, setFormData] = useState({
        name: '',
        currency: 'USD',
        date_format: 'MM/DD/YYYY'
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    // Add escape key functionality
    useEscapeKey(isOpen, onClose, !loading)

    // Available currencies
    const currencies = [
        { value: 'USD', label: 'USD - US Dollar' },
        { value: 'EUR', label: 'EUR - Euro' },
        { value: 'GBP', label: 'GBP - British Pound' },
        { value: 'INR', label: 'INR - Indian Rupee' },
        { value: 'CAD', label: 'CAD - Canadian Dollar' },
        { value: 'AUD', label: 'AUD - Australian Dollar' },
        { value: 'JPY', label: 'JPY - Japanese Yen' },
        { value: 'CHF', label: 'CHF - Swiss Franc' }
    ]

    // Initialize form data when modal opens
    useEffect(() => {
        if (isOpen && initialData) {
            setFormData({
                name: initialData.name || '',
                currency: initialData.currency || 'USD',
                date_format: initialData.date_format || 'MM/DD/YYYY'
            })
        } else if (isOpen) {
            // Reset form for new client
            setFormData({
                name: '',
                currency: 'USD',
                date_format: 'MM/DD/YYYY'
            })
        }

        // Clear error state when modal opens
        setError(null)
    }, [isOpen, initialData, isEditMode])

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
        
        // Clear error when user starts typing
        if (error) {
            setError(null)
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        // Validation
        if (!formData.name.trim()) {
            setError('Client name is required')
            return
        }

        if (!formData.currency) {
            setError('Currency is required')
            return
        }

        try {
            setLoading(true)
            setError(null)
            
            if (!apiToken) {
                setError('Authentication token missing')
                return
            }

            apiClient.setAuthToken(apiToken)
            
            const clientData = {
                name: formData.name.trim(),
                currency: formData.currency,
                date_format: formData.date_format
            }

            let savedClient
            if (isEditMode && initialData?.id) {
                // Update existing client
                const response = await apiClient.updateClient(initialData.id, clientData)
                savedClient = response.client
            } else {
                // Create new client
                const response = await apiClient.createClient(clientData)
                savedClient = response.client
            }

            onSave(savedClient)
            onClose()
        } catch (err) {
            console.error('Failed to save client:', err)
            setError(err.message || 'Failed to save client')
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        setError(null)
        onClose()
    }

    if (!isOpen) return null

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>{isEditMode ? 'Edit Client' : 'Create New Client'}</h2>
                    <button 
                        className={styles.closeButton}
                        onClick={handleCancel}
                        aria-label="Close modal"
                        disabled={loading}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    {error && (
                        <div className={styles.errorMessage}>
                            {error}
                        </div>
                    )}

                    <div className={styles.formGroup}>
                        <label htmlFor="clientName" className={styles.label}>
                            Client Name *
                        </label>
                        <input
                            type="text"
                            id="clientName"
                            className={styles.input}
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="Enter client name"
                            autoFocus
                            disabled={loading}
                            maxLength={255}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="currency" className={styles.label}>
                            Currency *
                        </label>
                        <select
                            id="currency"
                            className={styles.select}
                            value={formData.currency}
                            onChange={(e) => handleInputChange('currency', e.target.value)}
                            disabled={loading}
                        >
                            {currencies.map(currency => (
                                <option key={currency.value} value={currency.value}>
                                    {currency.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="dateFormat" className={styles.label}>
                            Date Format *
                        </label>
                        <select
                            id="dateFormat"
                            className={styles.select}
                            value={formData.date_format}
                            onChange={(e) => handleInputChange('date_format', e.target.value)}
                            disabled={loading}
                        >
                            <option value="MM/DD/YYYY">MM/DD/YYYY (US Format)</option>
                            <option value="DD/MM/YYYY">DD/MM/YYYY (UK Format)</option>
                            <option value="YYYY-MM-DD">YYYY-MM-DD (ISO Format)</option>
                            <option value="DD.MM.YYYY">DD.MM.YYYY (German Format)</option>
                            <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                        </select>
                    </div>

                    <div className={styles.buttonRow}>
                        <div className={styles.leftButtons}>
                            {/* Space for future delete button */}
                        </div>
                        <div className={styles.rightButtons}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={styles.saveButton}
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : (isEditMode ? 'Update Client' : 'Create Client')}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}